import React from 'react';
import { 
  CameraIcon, 
  SparklesIcon, 
  StarIcon, 
  ChartBarIcon,
  ShieldCheckIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import { HomeContent, PublicFooter } from '../../seo/PublicContent';
import { intro, pages } from '../../seo/content';

const WelcomePage: React.FC = () => {
  const features = [
    {
      icon: CameraIcon,
      title: 'Smart Photo Analysis',
      description: 'Snap photos or upload images for instant AI-powered analysis',
      color: 'from-blue-500 to-blue-600'
    },
    {
      icon: SparklesIcon,
      title: 'AI Value Estimation',
      description: 'Research estimated values and check matching listings',
      color: 'from-purple-500 to-purple-600'
    },
    {
      icon: StarIcon,
      title: 'Track Your Collection',
      description: 'Save and organize your items with detailed analysis history',
      color: 'from-amber-500 to-amber-600'
    },
    {
      icon: ChartBarIcon,
      title: 'Live eBay Research',
      description: 'Use matching live eBay listings to support realistic resale guidance',
      color: 'from-green-500 to-green-600'
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 relative overflow-hidden">
      <header className="relative z-20 px-4 pt-4 text-white sm:px-6 sm:pt-6 lg:px-8">
        <nav aria-label="Main navigation" className="mx-auto flex max-w-7xl flex-col items-center gap-3 rounded-2xl border border-white/10 bg-slate-900/50 p-3 shadow-lg shadow-slate-950/20 backdrop-blur-xl sm:flex-row sm:justify-between sm:px-6">
          <a href="/" aria-current="page" className="rounded-lg px-2 py-2 text-lg font-bold tracking-tight text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-300">Thriftalyze</a>
          <div className="flex items-center gap-2 rounded-xl bg-white/5 p-1">
            <a href="/how-it-works" className="rounded-lg px-4 py-2.5 text-sm font-medium text-slate-200 transition-colors hover:bg-white/10 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-300">How it works</a>
            <a href="/signin" className="rounded-lg border border-blue-400/30 bg-blue-500/15 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-500/30 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-300">Sign in</a>
          </div>
        </nav>
      </header>
      {/* Video Background */}
      <div className="absolute inset-0 z-0">
        <video 
          autoPlay 
          muted 
          loop 
          playsInline
          className="absolute inset-0 w-full h-full object-cover transition-all duration-700"
          style={{ filter: 'brightness(0.2) blur(1px)' }}
        >
          <source src="/snapalyze video.mp4" type="video/mp4" />
        </video>
        {/* Enhanced gradient overlay for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/80 via-blue-900/70 to-indigo-900/80" />
      </div>

      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10 z-10">
        <div className="absolute inset-0 bg-repeat" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}></div>
      </div>
      
      {/* Hero Section */}
      <main id="public-main" className="relative z-20 flex flex-col justify-center min-h-screen px-4 sm:px-6 lg:px-8 pt-4 pb-12">
        <div className="max-w-7xl mx-auto w-full">
          {/* Header */}
          <div className="text-center mb-16">
            {/* Simple Floating Logo */}
            <div className="flex justify-center mb-3">
              <div className="relative">
                <img 
                  src="/thriftalyze-logo.png"
                  alt="Thriftalyze"
                  width="320" height="320"
                  className="w-56 h-56 md:w-80 md:h-80 object-contain rounded-3xl animate-float-gentle drop-shadow-2xl"
                />
                {/* Subtle glow effect that won't be cut off */}
                <div className="absolute inset-0 bg-gradient-to-r from-blue-400/20 to-purple-400/20 rounded-full blur-xl animate-pulse scale-110 -z-10"></div>
              </div>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 tracking-tight">
              <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                {pages[0].h1}
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-slate-300 mb-8 max-w-3xl mx-auto leading-relaxed">
              {intro}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <a
                href="/analyze"
                className="group relative px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-2xl shadow-2xl hover:shadow-blue-500/25 transform hover:-translate-y-1 transition-all duration-300 flex items-center justify-center space-x-2"
              >
                <span>Analyze an item</span>
                <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </a>
              
              <a
                href="/signin"
                className="px-8 py-4 border-2 border-slate-500 text-slate-300 font-semibold rounded-2xl hover:border-white hover:text-white hover:bg-white/5 transition-all duration-300"
              >
                Sign In
              </a>
            </div>

          </div>

          {/* Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className="group bg-white/5 backdrop-blur-lg border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all duration-300 hover:-translate-y-2"
              >
                <div className={`inline-flex items-center justify-center w-12 h-12 mb-4 bg-gradient-to-r ${feature.color} rounded-xl shadow-lg`}>
                  <feature.icon className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-xl font-semibold text-white mb-3">{feature.title}</h2>
                <p className="text-slate-300 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>

          <HomeContent />

          {/* Bottom CTA */}
          <div className="text-center mb-8">
            <p className="text-slate-400 mb-6">
              Turn your next unfamiliar find into useful research
            </p>
            <div className="flex justify-center space-x-8 pb-4">
              <div className="flex items-center space-x-2 text-slate-300">
                <ShieldCheckIcon className="w-5 h-5 text-green-400" />
                <span>Secure & Private</span>
              </div>
              <div className="flex items-center space-x-2 text-slate-300">
                <SparklesIcon className="w-5 h-5 text-purple-400" />
                <span>AI Powered</span>
              </div>
            </div>
          </div>
        </div>
      </main>
      <div className="relative z-20"><PublicFooter /></div>
    </div>
  );
};

export default WelcomePage;
